import { t } from "@travily/i18n";
import type { ProgramsCardMapLocationsTrait } from "@travily/types/models/ProgramsCard";
import { Icon } from "@ui/components/Icon";
import { type LngLatBoundsLike, Marker } from "@ui/components/Map";
import { SectionCard } from "@ui/components/SectionCard";
import { useMemo } from "react";
import { useMap } from "@/layouts/map-host";

const NS = "components.views.programs.show";

// i18n-tasks-use t('components.views.programs.show.location_title')
// i18n-tasks-use t('components.views.programs.show.location_description')

type MapLocation = ProgramsCardMapLocationsTrait["map_locations"][number];

/** Padding for a lone extent-less point so it doesn't zoom to street level. */
const POINT_PAD = 0.5;

/**
 * Frames every map location: the envelope of each entry's bbox (or bare point),
 * padded when the result is a single point so the fit keeps some context.
 */
function computeFitBounds(locations: MapLocation[]): LngLatBoundsLike | undefined {
  const first = locations[0];
  if (!first) return undefined;
  let west = Number.POSITIVE_INFINITY;
  let south = Number.POSITIVE_INFINITY;
  let east = Number.NEGATIVE_INFINITY;
  let north = Number.NEGATIVE_INFINITY;
  for (const location of locations) {
    const [w, s, e, n] = location.bbox ?? [location.lon, location.lat, location.lon, location.lat];
    if (w < west) west = w;
    if (s < south) south = s;
    if (e > east) east = e;
    if (n > north) north = n;
  }
  if (west === east && south === north) {
    return [
      [west - POINT_PAD, south - POINT_PAD],
      [east + POINT_PAD, north + POINT_PAD],
    ];
  }
  return [
    [west, south],
    [east, north],
  ];
}

/**
 * The interactive map body. Split out so `useMap` (which needs a client-side
 * `MapHost` ancestor and drives the shared singleton's global view state) is
 * only called on the one active, mounted instance — never during SSR.
 */
function LocationMap({ locations }: { locations: MapLocation[] }) {
  const fitBounds = useMemo(() => computeFitBounds(locations), [locations]);

  // Decorative pins only — the location is named in the page's subline, so the
  // markers carry no interaction and stay hidden from AT.
  const overlay = useMemo(
    () =>
      locations.map((location) => (
        <Marker
          anchor="bottom"
          key={`${location.lat},${location.lon}`}
          latitude={location.lat}
          longitude={location.lon}
        >
          <span aria-hidden className="block">
            <Icon name="location" size="32" type="sticker" />
          </span>
        </Marker>
      )),
    [locations],
  );

  const slotRef = useMap({
    overlay,
    className: "size-full",
    fitBounds,
    // Bottom-right keeps the controls clear of the gradient fading the card
    // background into the map's top edge.
    controlsPosition: "bottom-right",
    // An in-flow map inside a scrolling page must not hijack the scroll gesture.
    cooperativeGestures: true,
  });

  return <div className="size-full" ref={slotRef} />;
}

export interface LocationCardProps {
  /** `program.map_locations`: destination-resolved pins with optional extents. */
  mapLocations: MapLocation[];
  /**
   * Whether this instance owns the map. The view renders the card twice (mobile
   * tab + desktop stack, split by CSS) but the Mapbox singleton allows only one
   * `useMap` consumer, so exactly one instance may be active — and only after
   * mount (SSR has no `MapHost`). The header still renders when inactive, so
   * the section copy stays in the server-rendered HTML.
   */
  mapActive: boolean;
  className?: string;
}

/**
 * "Where Your Adventure Begins" program section: a blue card whose lower half
 * is a live map of the program's destination (falling back to its raw
 * location), with the card background fading into the map's top edge. Renders
 * nothing when the program has no locatable place.
 */
export function LocationCard({ mapLocations, mapActive, className }: LocationCardProps) {
  if (mapLocations.length === 0) return null;

  return (
    <SectionCard className={className} theme="blue">
      {/* No bottom padding: the map's top gradient starts right at the description,
          fading the card background into the map like the Figma composition. */}
      <SectionCard.Content className="pb-0 xl:pb-0">
        <SectionCard.Header
          description={t(`${NS}.location_description`)}
          icon={<Icon className="size-8 xl:size-10" name="location" type="sticker" />}
          title={t(`${NS}.location_title`)}
        />
      </SectionCard.Content>
      <div className="relative h-64 w-full xl:h-108">
        {mapActive && <LocationMap locations={mapLocations} />}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 z-10 h-16 bg-gradient-to-b from-blue-50 to-transparent"
        />
      </div>
    </SectionCard>
  );
}
